// Per-ad hero copy for /lifetime (message match).
//
// The ad's utm_content (the ad name) picks which eyebrow, headline and subhead
// the first screen shows. Price, CTA and everything below the hero never
// change. The prerendered HTML always carries the DEFAULT hero; the client
// reads utm_content on its first render (main.tsx uses createRoot, not
// hydrateRoot, so there is no hydration mismatch to avoid).
//
// Every line below is lifted from a live ad, the /lifetime page or repo copy.
// Source table: the PR description. Rules: no "15" (lifetime is 5 a day), no
// arrows, no em dashes.

export type HeroVariantId = "default" | "daily-ideas" | "what-to-test" | "fatigue-cost" | "story";

export interface HeroCopy {
  eyebrow: string;
  /** Headline text before the italic tail. */
  h1: string;
  /** Optional italic tail of the headline (default hero only). */
  h1Em?: string;
  sub: string;
}

export const HERO_COPY: Record<HeroVariantId, HeroCopy> = {
  default: {
    eyebrow: "For people running Meta ads today",
    h1: "Paste a competitor. Get their best ad, ",
    h1Em: "rebuilt for your brand.",
    sub: "Your competitors already paid to find out which ads work. Mockingbird pulls the one they kept running, rebuilds it with your logo, colours and voice, and hands you export-ready variants in minutes.",
  },
  "daily-ideas": {
    eyebrow: "5 fresh competitor ads, every morning",
    h1: "A fresh batch of ad ideas, every day.",
    sub: "Your next ad test comes from what is already running, not a blank page. Mockingbird rebuilds the ads your competitors kept running with your logo, colours and voice, ready to export.",
  },
  "what-to-test": {
    eyebrow: "Skip the blank page",
    h1: "Staring at Ads Manager with no idea what to test next?",
    sub: "A fresh, scored idea lands every morning. Mockingbird rebuilds the ad your competitor has kept running with your logo, colours and voice, ready to upload.",
  },
  "fatigue-cost": {
    eyebrow: "Ad fatigue",
    h1: "Every ad wears out. A designer costs $15 to $60 per new ad.",
    sub: "Mockingbird rebuilds a competitor's longest-running ad with your logo, colours and voice, in feed and story sizes, ready to upload.",
  },
  story: {
    eyebrow: "Why we built Mockingbird",
    h1: "We burned hours every week making ad variants for our own product.",
    sub: "So we built a tool that starts from a competitor's ad instead of a blank page, and rebuilds it with your logo, colours and voice, ready to upload.",
  },
};

/** Exact ad names. Mechanism, price and paste-a-competitor ads stay on default. */
export const AD_TO_VARIANT: Record<string, HeroVariantId> = {
  "mb-a-week-vertical": "daily-ideas",
  "mb-static-screen": "daily-ideas",
  "mb-sounds-like-you-vertical": "what-to-test",
  "mb-static-problem-test": "what-to-test",
  "mb-signals-vertical": "what-to-test",
  "mb-static-problem-fatigue": "fatigue-cost",
  "mb-static-unaware": "story",
  "mb-static-price": "default",
  "mb-carousel-steps": "default",
  "mb-fast-intro-vertical": "default",
  "mb-before-after-60s-vertical": "default",
  "mb-strategist-vertical": "default",
  "mb-cmo-brain-vertical": "default",
  "mb-five-steps-60s-vertical": "default",
  "mb-ad-its-public-vertical": "default",
  "mb-ad-three-moves-vertical": "default",
};

/** Prefix fallback so new cuts of a concept (e.g. -landscape, -v2) keep their hero. */
const PREFIX_TO_VARIANT: ReadonlyArray<readonly [string, HeroVariantId]> = [
  ["mb-a-week", "daily-ideas"],
  ["mb-static-screen", "daily-ideas"],
  ["mb-sounds-like-you", "what-to-test"],
  ["mb-static-problem-test", "what-to-test"],
  ["mb-signals", "what-to-test"],
  ["mb-static-problem-fatigue", "fatigue-cost"],
  ["mb-static-unaware", "story"],
];

export function variantForAd(utmContent: string | null | undefined): HeroVariantId {
  const name = (utmContent ?? "").trim().toLowerCase();
  if (!name) return "default";
  if (Object.prototype.hasOwnProperty.call(AD_TO_VARIANT, name)) return AD_TO_VARIANT[name];
  for (const [prefix, variant] of PREFIX_TO_VARIANT) {
    if (name.startsWith(prefix)) return variant;
  }
  return "default";
}

export const UTM_CONTENT_KEY = "mb_utm_content";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * A utm_content in the URL wins and is remembered for the tab session, so a
 * reload or an in-site navigation back to /lifetime keeps the same hero. With
 * no utm_content in the URL, the stored one is used. Storage errors degrade to
 * URL-only.
 */
export function resolveHeroVariant(search: string, storage: StorageLike | null): HeroVariantId {
  const fromUrl = new URLSearchParams(search).get("utm_content");
  if (fromUrl) {
    try {
      storage?.setItem(UTM_CONTENT_KEY, fromUrl);
    } catch {
      // private mode: URL-only
    }
    return variantForAd(fromUrl);
  }
  try {
    return variantForAd(storage?.getItem(UTM_CONTENT_KEY));
  } catch {
    return "default";
  }
}

/** Browser entry point. Returns "default" on the server (prerender). */
export function getHeroVariant(): HeroVariantId {
  if (typeof window === "undefined") return "default";
  let storage: StorageLike | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return resolveHeroVariant(window.location.search, storage);
}
